import React from 'react';
import Localize from 'react-intl-universal';

import CancelIcon from '@mui/icons-material/Cancel';
import { useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Step from '@mui/material/Step';
import StepIcon from '@mui/material/StepIcon';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import { styled, useTheme } from '@mui/material/styles';

const StyledStepper = styled(Stepper)(({ theme }) => ({
  overflow: 'auto',
  [theme.breakpoints.down('md')]: {
    justifyContent: 'center'
  },
  [theme.breakpoints.up('md')]: {
    paddingLeft: '1rem',
    paddingRight: '1rem',
    '& > .MuiStep-root': {
      minWidth: 0
    }
  }
}));

const CustomStepIcon = ({ icon }) => {
  return <CancelIcon style={{ opacity: 0.5 }}>{icon}</CancelIcon>;
};

// Step icon that always shows numbers
const StyledNumberStepIconRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: '50%',
  color: theme.palette.common.white,
  fontWeight: 600,
  fontSize: 14,
  backgroundColor: theme.palette.primary.main,
  marginLeft: '10px',
  marginRight: '10px'
}));

const StyledBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.booking.background,
  marginLeft: '10px',
  marginRight: '10px'
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  position: 'relative',
  top: '15px',
  marginLeft: '3rem',
  marginRight: '3rem',
  [theme.breakpoints.up('md')]: {
    display: 'none'
  }
}));

const StyledStepIcon = styled(StepIcon)(({ theme }) => ({
  text: { fill: theme.palette.common.white }
}));

const NumberStepIcon = ({ step }) => {
  return (
    <StyledBox>
      <StyledNumberStepIconRoot>{step?.id + 1}</StyledNumberStepIconRoot>
    </StyledBox>
  );
};

const Slider = ({ steps = [], activeStep, availableSteps }) => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box>
      <StyledDivider />
      <StyledStepper activeStep={availableSteps[activeStep]} alternativeLabel>
        {steps.map((step, index) => {
          if (isCompact && availableSteps[activeStep] !== step.id) {
            return null; // show only the active step when labels cannot fit
          }

          return (
            <Step
              key={step.id}
              sx={(theme) => ({
                '& .MuiStepLabel-iconContainer': {
                  opacity: !availableSteps.includes(step.id) ? 0.2 : 1
                },

                '& .MuiStepLabel-label': {
                  color: !availableSteps.includes(step.id)
                    ? theme.palette.text.secondary
                    : theme.palette.text.primary,
                  opacity: 1,
                  transition: theme.transitions.create('color')
                },

                '& .MuiStepLabel-label.Mui-disabled': {
                  color: theme.palette.text.secondary,
                  opacity: 1
                },

                '& .MuiStepLabel-label.Mui-completed': {
                  color: theme.palette.text.primary,
                  opacity: 1
                }
              })}
            >
              <StepLabel
                StepIconComponent={(props) => {
                  return !availableSteps.includes(step.id) ? (
                    <CustomStepIcon {...props} active={index === activeStep} />
                  ) : isCompact ? (
                    <NumberStepIcon step={step} />
                  ) : (
                    <StyledStepIcon {...props} />
                  );
                }}
              >
                {Localize.get(`Booking.${step.name}`)}
              </StepLabel>
            </Step>
          );
        })}
      </StyledStepper>
    </Box>
  );
};

export default Slider;
